import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Button, Modal, Form, Input, Select, DatePicker, Radio, message, Tabs, Table, Space, Tag, Row, Col, Drawer, Descriptions, Timeline, Spin, Card, Grid, List } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { congViecService, CongViecCreatePayload, WorkflowActionPayload, CongViecDto } from '../services/congViecService';
import { masterDataService, Employee as EmployeeType, Department } from '../services/masterDataService';
import { authService } from '../services/authService';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

interface Employee {
    macc: string;
    tennv: string;
    maChamCong?: string;
    tenNV?: string;
}

interface CongViecLichSuRecord {
    id: number;
    tenNguoiThucHien: string;
    hanhDong: string;
    ghiChu?: string;
    thoiGianThucHien: string;
    duLieuThem?: string;
}

const CongViecPage: React.FC = () => {
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [isWorkflowModalVisible, setIsWorkflowModalVisible] = useState(false);
    const [workflowAction, setWorkflowAction] = useState<{ type: 'forward' | 'reject' | 'return' | 'process', taskId: number | null }>({ type: 'forward', taskId: null });
    
    const [loading, setLoading] = useState(false);
    const [allTasks, setAllTasks] = useState<CongViecDto[]>([]);
    const [currentUserMacc, setCurrentUserMacc] = useState<string | null>(null);
    const [involvedTasks, setInvolvedTasks] = useState<CongViecDto[]>([]);

    // Data for Selects
    const [taskTypes, setTaskTypes] = useState<{ id: number, tenLoai: string }[]>([]);
    const [recipients, setRecipients] = useState<EmployeeType[]>([]);
    const [allEmployees, setAllEmployees] = useState<EmployeeType[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);

    // Detail Drawer state
    const [isDetailVisible, setIsDetailVisible] = useState(false);
    const [selectedTask, setSelectedTask] = useState<CongViecDto | null>(null);
    const [taskHistory, setTaskHistory] = useState<CongViecLichSuRecord[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    
    const [createForm] = Form.useForm();
    const [workflowForm] = Form.useForm();
    const screens = useBreakpoint();

    const fetchData = async () => {
        setLoading(true);
        try {
            const [tasksRes, userProfileRes, allEmpsRes, taskTypesRes, departmentsRes, involvedTasksRes] = await Promise.all([
                congViecService.getTasks(),
                authService.getProfile(),
                masterDataService.getEmployees(),
                congViecService.getAllLoaiCongViec(),
                masterDataService.getDepartments(),
                congViecService.getInvolvedTasks(),
            ]);
            setAllTasks(tasksRes.data);
            setCurrentUserMacc(userProfileRes.macc);
            setAllEmployees(allEmpsRes.data);
            setTaskTypes(taskTypesRes.data);
            setDepartments(departmentsRes.data);
            setInvolvedTasks(involvedTasksRes.data);
        } catch (error) {
            message.error("Lỗi khi tải dữ liệu.");
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        fetchData();
    }, []);

    const { actionableTasks, sentTasks } = useMemo((): { actionableTasks: CongViecDto[], sentTasks: CongViecDto[] } => {
        if (!currentUserMacc) return { actionableTasks: [], sentTasks: [] };
        
        const actionable = allTasks.filter(task => 
            (task.maNguoiXuLyHienTai === currentUserMacc && (task.trangThai === 0 || task.trangThai === 4)) || // New or Returned task assigned to me
            (task.maNguoiTao === currentUserMacc && task.trangThai === 1 && task.maNguoiXuLyHienTai === currentUserMacc) // Processed task waiting for my confirmation
        );
        const sent = allTasks.filter(task => task.maNguoiTao === currentUserMacc);
        return { actionableTasks: actionable, sentTasks: sent };
    }, [allTasks, currentUserMacc]);

    const handleTaskTypeChange = async (loaiCongViecId: number) => {
        createForm.setFieldsValue({ maNguoiNhan: undefined });
        try {
            const response = await congViecService.getRecipientsByLoaiCV(loaiCongViecId);
            const formattedRecipients = response.data.map((recipient: any) => ({
                macc: recipient.maChamCong || recipient.macc,
                tennv: recipient.tenNV || recipient.tennv,
                ...recipient
            }));
            setRecipients(formattedRecipients);
        } catch (error) {
            message.error("Không thể tải danh sách người nhận.");
        }
    };

    const handleCreate = async (values: any) => {
        setLoading(true);
        try {
            const payload: CongViecCreatePayload = { ...values, ngayHetHan: values.ngayHetHan ? (values.ngayHetHan as Dayjs).format('YYYY-MM-DD') : undefined };
            await congViecService.createTask(payload);
            message.success("Tạo công việc thành công!");
            setIsCreateModalVisible(false);
            createForm.resetFields();
            fetchData();
        } catch (error) {
            message.error("Tạo công việc thất bại.");
        } finally {
            setLoading(false);
        }
    };
    
    const handleWorkflowSubmit = async (values: any) => {
        if (!workflowAction.taskId) return;
        setLoading(true);
        try {
            const payload: WorkflowActionPayload = { ...values };
            switch (workflowAction.type) {
                case 'forward':
                    await congViecService.chuyenTiep(workflowAction.taskId, payload);
                    break;
                case 'reject':
                    await congViecService.tuChoi(workflowAction.taskId, payload);
                    break;
                case 'return':
                    await congViecService.traLai(workflowAction.taskId, payload);
                    break;
                case 'process': // New case for 'Xử lý' action
                    await congViecService.xuLy(workflowAction.taskId, payload);
                    break;
            }
            message.success("Thực hiện hành động thành công!");
            setIsWorkflowModalVisible(false);
            workflowForm.resetFields();
            fetchData();
        } catch (error: any) {
             message.error(error?.response?.data || "Thực hiện hành động thất bại.");
        }
    };

    const handleSimpleAction = async (action: 'process' | 'complete' | 'cancel', taskId: number, ghiChu?: string) => {
        setLoading(true);
        try {
            const payload: WorkflowActionPayload = { ghiChu };
            switch(action) {
                case 'process': await congViecService.xuLy(taskId, payload); break;
                case 'complete': await congViecService.hoanThanh(taskId, payload); break;
                case 'cancel': await congViecService.huyBo(taskId, payload); break;
            }
            message.success("Thực hiện hành động thành công!");
            fetchData();
        } catch(error: any) {
            message.error(error?.response?.data || "Thực hiện hành động thất bại.");
        } finally {
            setLoading(false);
        }
    }

    const showDetailDrawer = async (record: CongViecDto) => {
        setHistoryLoading(true);
        setSelectedTask(record);
        setIsDetailVisible(true);
        try {
            const historyResponse = await congViecService.getTaskHistory(record.id);
            setTaskHistory(historyResponse.data);
        } catch (error) {
            message.error('Không thể tải lịch sử công việc.');
        } finally {
            setHistoryLoading(false);
        }
    };

    const closeDetailDrawer = () => {
        setIsDetailVisible(false);
        setSelectedTask(null);
        setTaskHistory([]);
    };

    const getActionButtons = (record: CongViecDto) => {
        const isCreator = record.maNguoiTao === currentUserMacc;
        const isCurrentHandler = record.maNguoiXuLyHienTai === currentUserMacc;

        const buttonStyle = screens.xs ? { width: '100%' } : {};
        const buttonSize = screens.xs ? 'middle' : 'small';

        return (
            <Space direction={screens.xs ? 'vertical' : 'horizontal'} style={{ width: '100%' }} size="middle">
                <Button style={buttonStyle} size={buttonSize} onClick={() => showDetailDrawer(record)}>Xem chi tiết</Button>
                
                {isCurrentHandler && (record.trangThai === 0 || record.trangThai === 4) && (
                    <>
                        <Button style={buttonStyle} type="primary" size={buttonSize} onClick={() => { setWorkflowAction({ type: 'process', taskId: record.id }); setIsWorkflowModalVisible(true); }}>Xử lý</Button>
                        <Button style={buttonStyle} size={buttonSize} onClick={() => handleForward(record.id)}>Chuyển tiếp</Button>
                        {!isCreator && <Button style={buttonStyle} size={buttonSize} danger onClick={() => handleReturn(record.id)}>Trả lại</Button>}
                    </>
                )}

                {isCreator && record.trangThai !== 2 && record.trangThai !== 3 && (
                     <Button style={buttonStyle} type="primary" size={buttonSize} onClick={() => handleComplete(record.id)}>Xác nhận hoàn thành</Button>
                )}

                {isCreator && record.trangThai === 1 && record.maNguoiXuLyHienTai === currentUserMacc && (
                    <Button style={buttonStyle} danger size={buttonSize} onClick={() => handleReject(record.id)}>Từ chối</Button>
                )}

                {isCreator && (record.trangThai === 0 || record.trangThai === 1) && (
                    <Button style={buttonStyle} type="link" danger size={buttonSize} onClick={() => handleCancel(record.id)}>Hủy</Button>
                )}
            </Space>
        );
    };

    const statusMap: { [key: number]: { color: string; text: string } } = {
        0: { color: 'blue', text: 'Mới' },
        1: { color: 'orange', text: 'Đang xử lý' },
        2: { color: 'green', text: 'Hoàn thành' },
        3: { color: 'default', text: 'Đã hủy' },
        4: { color: 'purple', text: 'Bị trả lại' },
    };

    // Helper function for deadline status styles
    const getDeadlineStatusStyles = (deadlineDate?: string, status?: number) => {
        let borderColor = 'transparent'; // Default
        let textColor = 'inherit';
        // Status 2: Hoàn thành. (Cancelled tasks should still show deadline status)
        const isDone = status === 2;

        if (deadlineDate && !isDone) {
            const deadline = dayjs(deadlineDate).startOf('day');
            const today = dayjs().startOf('day');
            
            if (today.isAfter(deadline)) {
                borderColor = '#ff4d4f'; // Ant Design Red for overdue
                textColor = '#ff4d4f';
            } else {
                const diffDays = deadline.diff(today, 'day');
                if (diffDays <= 1) { // Today or Tomorrow
                    borderColor = '#faad14'; // Ant Design Orange for nearing
                    textColor = '#faad14';
                } else if (diffDays <= 4) {
                    borderColor = '#fadb14'; // Ant Design Yellow for nearing
                    textColor = '#fadb14';
                }
            }
        }
        return { borderColor, textColor };
    };
    const columns = (listType: 'actionable' | 'sent' | 'involved'): ColumnsType<CongViecDto> => [
        { title: 'Tiêu Đề', dataIndex: 'tieuDe', key: 'tieuDe', width: 250 },
        { title: listType === 'actionable' ? 'Người Gửi' : 'Người Xử Lý', dataIndex: listType === 'actionable' ? 'tenNguoiTao' : 'tenNguoiXuLyHienTai', key: 'nguoiLienQuan' },
        { title: 'Ngày Gửi/Nhận', dataIndex: 'ngayTao', key: 'ngayTao', render: (text:string) => dayjs(text).format('HH:mm DD/MM/YYYY') },
        { title: 'Deadline', dataIndex: 'ngayHetHan', key: 'ngayHetHan', render: (text, record) => {
            if (!text) return 'N/A';
            const { textColor } = getDeadlineStatusStyles(record.ngayHetHan, record.trangThai);
            return (
                <span style={{ color: textColor }}>
                    {dayjs(text).format('DD/MM/YYYY')}
                </span>
            );
        }, sorter: (a, b) => dayjs(a.ngayHetHan).unix() - dayjs(b.ngayHetHan).unix(), defaultSortOrder: 'ascend' },
        { title: 'Trạng Thái', dataIndex: 'trangThai', key: 'trangThai', render: (status:number) => {
            const statusMap: { [key: number]: { color: string; text: string } } = {
                0: { color: 'blue', text: 'Mới' },
                1: { color: 'orange', text: 'Đang xử lý' },
                2: { color: 'green', text: 'Hoàn thành' },
                3: { color: 'default', text: 'Đã hủy' },
                4: { color: 'purple', text: 'Bị trả lại' },
            };
            return <Tag color={statusMap[status]?.color || 'default'}>{statusMap[status]?.text || 'Không xác định'}</Tag>;
        } },
        { title: 'Hành Động', key: 'action', fixed: 'right', render: (_: any, record: CongViecDto) => getActionButtons(record) }
    ];

    const renderTaskList = (tasks: CongViecDto[], listType: 'actionable' | 'sent' | 'involved') => {
        if (!screens.xs) {
            return <Table dataSource={tasks} columns={columns(listType)} rowKey="id" loading={loading} scroll={{ x: 'max-content' }}/>;
        }

        const statusMap: { [key: number]: { color: string; text: string } } = {
            0: { color: 'blue', text: 'Mới' },
            1: { color: 'orange', text: 'Đang xử lý' },
            2: { color: 'green', text: 'Hoàn thành' },
            3: { color: 'default', text: 'Đã hủy' },
            4: { color: 'purple', text: 'Bị trả lại' },
        };

        return (
            <List
                grid={{ gutter: 16, xs: 1 }}
                dataSource={tasks}
                loading={loading}
                renderItem={item => {
                    const { borderColor, textColor } = getDeadlineStatusStyles(item.ngayHetHan, item.trangThai);
                    const cardStyle: React.CSSProperties = { marginTop: 16, borderColor: borderColor, borderWidth: 2 };

                    return (
                    <List.Item>
                        <Card title={item.tieuDe} style={cardStyle}>
                            <p><strong>{listType === 'actionable' ? 'Người Gửi:' : 'Người Xử Lý:'}</strong> {listType === 'actionable' ? item.tenNguoiTao : item.tenNguoiXuLyHienTai}</p>
                            <p><strong>Ngày:</strong> {dayjs(item.ngayTao).format('HH:mm DD/MM/YYYY')}</p>
                            {item.ngayHetHan && (
                                <p><strong>Deadline: </strong>
                                    <span style={{ color: textColor, fontWeight: 'bold' }}>
                                        {dayjs(item.ngayHetHan).format('DD/MM/YYYY')}
                                    </span>
                                </p>
                            )}
                            <p><strong>Trạng thái:</strong> <Tag color={statusMap[item.trangThai]?.color}>{statusMap[item.trangThai]?.text}</Tag></p>
                            <div style={{ marginTop: 16 }}>
                               {getActionButtons(item)}
                            </div>
                        </Card>
                    </List.Item>
                    );
                }}
            />
        );
    };

    const handleComplete = async (id: number) => {
        try {
            await congViecService.hoanThanh(id, { ghiChu: 'Đã hoàn thành' });
            message.success('Đã xác nhận hoàn thành công việc');
            fetchData();
        } catch (error) {
            message.error('Có lỗi xảy ra khi xác nhận hoàn thành');
        }
    };

    const handleReject = (id: number) => {
        setWorkflowAction({ type: 'reject', taskId: id });
        setIsWorkflowModalVisible(true);
    };

    const handleForward = (id: number) => {
        setWorkflowAction({ type: 'forward', taskId: id });
        setIsWorkflowModalVisible(true);
    };

    const handleReturn = (id: number) => {
        setWorkflowAction({ type: 'return', taskId: id });
        setIsWorkflowModalVisible(true);
    };

    const handleCancel = async (id: number) => {
        try {
            await congViecService.huyBo(id, { ghiChu: 'Đã hủy' });
            message.success('Đã hủy công việc');
            fetchData();
        } catch (error) {
            message.error('Có lỗi xảy ra khi hủy công việc');
        }
    };

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Title level={4}>Công Việc Cá Nhân</Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => { createForm.resetFields(); setIsCreateModalVisible(true); }}>
                    Tạo công việc mới
                </Button>
            </div>

            <Tabs defaultActiveKey="1" type="card" centered>
                <TabPane tab={`Cần xử lý (${actionableTasks.length})`} key="1">
                    {renderTaskList(actionableTasks, 'actionable')}
                </TabPane>
                <TabPane tab={`Đã gửi (${sentTasks.length})`} key="2">
                   {renderTaskList(sentTasks, 'sent')}
                </TabPane>
                <TabPane tab={`Lịch sử (${involvedTasks.length})`} key="3">
                    {renderTaskList(involvedTasks, 'involved')}
                </TabPane>
            </Tabs>

            <Modal title="Tạo công việc mới" open={isCreateModalVisible} onCancel={() => setIsCreateModalVisible(false)} footer={null} destroyOnClose width={800}>
                <Form form={createForm} layout="vertical" onFinish={handleCreate}>
                    <Form.Item name="tieuDe" label="Tiêu đề" rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="noiDung" label="Nội dung chi tiết">
                        <Input.TextArea rows={4} />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="loaiCongViecId" label="Loại công việc" rules={[{ required: true, message: 'Vui lòng chọn loại công việc' }]}>
                                <Select placeholder="Chọn loại công việc" onChange={handleTaskTypeChange}>
                                    {taskTypes.map(type => <Option key={type.id} value={type.id}>{type.tenLoai}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="maNguoiNhan" label="Người tiếp nhận" rules={[{ required: true, message: 'Vui lòng chọn người nhận' }]}>
                                <Select 
                                    placeholder="Chọn người nhận" 
                                    showSearch 
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {recipients.map(r => (
                                        <Option key={r.macc} value={r.macc}>
                                            {r.tennv} - {r.macc}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                             <Form.Item name="ngayHetHan" label="Deadline" rules={[{ required: true, message: 'Vui lòng chọn deadline' }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                             <Form.Item name="mucDoUuTien" label="Mức độ ưu tiên" initialValue={0}>
                                <Radio.Group>
                                    <Radio value={0}>Thấp</Radio>
                                    <Radio value={1}>Trung bình</Radio>
                                    <Radio value={2}>Cao</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={loading}>Tạo</Button>
                    </Form.Item>
                </Form>
            </Modal>
            
            <Modal title={
                    workflowAction.type === 'forward' ? 'Chuyển tiếp công việc' 
                    : workflowAction.type === 'reject' ? 'Từ chối công việc' 
                    : workflowAction.type === 'process' ? 'Xử lý công việc'
                    : 'Trả lại công việc'
                } 
                open={isWorkflowModalVisible} 
                onCancel={() => setIsWorkflowModalVisible(false)} 
                footer={null} 
                destroyOnClose>
                <Form form={workflowForm} layout="vertical" onFinish={handleWorkflowSubmit}>
                    {workflowAction.type === 'forward' && (
                         <Form.Item name="maNguoiNhanMoi" label="Người tiếp nhận mới" rules={[{ required: true, message: 'Vui lòng chọn người nhận' }]}>
                            <Select 
                                placeholder="Chọn người nhận" 
                                showSearch 
                                optionFilterProp="children"
                                filterOption={(input, option) => 
                                    String(option?.children).toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {allEmployees.map(emp => (
                                    <Option key={emp.macc} value={emp.macc}>
                                        {emp.tennv} - {emp.macc}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    <Form.Item name="ghiChu" label="Ghi chú">
                        <Input.TextArea rows={4} />
                    </Form.Item>
                     <Form.Item>
                        <Button type="primary" htmlType="submit" loading={loading}>Xác nhận</Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Drawer
                title="Chi tiết công việc"
                width={screens.md ? 720 : '90%'}
                onClose={closeDetailDrawer}
                open={isDetailVisible}
                destroyOnClose
            >
                {selectedTask && (
                    <Spin spinning={historyLoading}>
                        <Descriptions bordered column={1} size="small">
                            <Descriptions.Item label="Tiêu đề">{selectedTask.tieuDe}</Descriptions.Item>
                            <Descriptions.Item label="Người tạo">{selectedTask.tenNguoiTao}</Descriptions.Item>
                            <Descriptions.Item label="Người xử lý hiện tại">{selectedTask.tenNguoiXuLyHienTai}</Descriptions.Item>
                            <Descriptions.Item label="Khoa/Phòng">{selectedTask.tenKhoaPhong}</Descriptions.Item>
                            <Descriptions.Item label="Ngày tạo">{dayjs(selectedTask.ngayTao).format('HH:mm DD/MM/YYYY')}</Descriptions.Item>
                            <Descriptions.Item label="Deadline">{selectedTask.ngayHetHan ? dayjs(selectedTask.ngayHetHan).format('HH:mm DD/MM/YYYY') : 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Nội dung">{selectedTask.noiDung || 'Không có'}</Descriptions.Item>
                        </Descriptions>

                        <Title level={5} style={{ marginTop: 24 }}>Lưu đồ xử lý</Title>
                        <Timeline>
                            {taskHistory.map(item => (
                                <Timeline.Item key={item.id}>
                                    <p><Text strong>{item.hanhDong}</Text> bởi <Text strong>{item.tenNguoiThucHien}</Text></p>
                                    <p>{dayjs(item.thoiGianThucHien).format('HH:mm DD/MM/YYYY')}</p>
                                    {item.ghiChu && <Card size="small" type="inner" style={{background: '#f5f5f5'}}>Ghi chú: {item.ghiChu}</Card>}
                                </Timeline.Item>
                            ))}
                        </Timeline>
                    </Spin>
                )}
            </Drawer>
        </div>
    );
};

export default CongViecPage;
